import type { Block, CalendarFeed, DisplayEvent, StyleVariant, Timezone } from './types';
import { MS_PER_DAY } from './time';
import { zonedParts } from './format';

// Day-blocking hatch helpers, shared by the horizontal timeline (per-feed rows +
// header band) and the 1W week grid (per-day columns). Kept as pure functions so
// both surfaces classify blocking identically.

// Resolve an event's effective style the same way EventPill does, so the
// row/header hatch matches what each pill renders as.
export function effectiveStyle(ev: DisplayEvent, feed: CalendarFeed): StyleVariant {
  if (ev.styleVariant !== 'none') return ev.styleVariant;
  if (feed.style) return feed.style;
  return 'none';
}

// Resolve an event's effective Block (the day-hatch scope), with a matching
// rule's block taking precedence over the calendar's own block — the same
// precedence rules use for style/color. A rule's 'off' (No block) is an
// explicit override that forces the event non-blocking even over a
// Global/Local calendar; 'off' is otherwise equivalent to 'none'.
// A cancelled event never inherits the calendar's block; only a filter's
// explicit block can still hatch it.
export function effectiveBlock(ev: DisplayEvent, feed: CalendarFeed): Block {
  if (ev.ruleBlock && ev.ruleBlock !== 'none') {
    return ev.ruleBlock === 'off' ? 'none' : ev.ruleBlock;
  }
  if (ev.cancelled) return 'none';
  const feedBlock = feed.block ?? 'none';
  return feedBlock === 'off' ? 'none' : feedBlock;
}

// Hatch density by effective style: prominent styles get the heavy hatch,
// tentative ones the discreet hatch, and struck/hidden contribute nothing.
export function hatchDensity(ev: DisplayEvent, feed: CalendarFeed): 'thick' | 'thin' | 'none' {
  const s = effectiveStyle(ev, feed);
  if (s === 'striked' || s === 'hidden') return 'none';
  if (s === 'dashed' || s === 'muted') return 'thin';
  return 'thick';
}

// Hatch density for a settings style-swatch (a feed or rule, no event). The ring
// previews the day-blocking *scope*: 'global' → thick (blocks the shared day
// across calendars), 'local' → thin (hatches just this lane). 'none'/'off' → no
// ring. Struck/hidden styles never hatch (matching hatchDensity), so they get no
// ring either. Density here encodes scope, not style — the style is already shown
// by the swatch box itself.
export function swatchHatch(block: Block, style: StyleVariant | undefined): 'thick' | 'thin' | 'none' {
  if (block !== 'global' && block !== 'local') return 'none';
  if (style === 'striked' || style === 'hidden') return 'none';
  return block === 'global' ? 'thick' : 'thin';
}

// Stable UTC "Y-M-D" key (no zero-padding) for a calendar day.
export function dayKeyOf(d: Date): string {
  return d.getUTCFullYear() + '-' + (d.getUTCMonth() + 1) + '-' + d.getUTCDate();
}

// The day-keys an event spans. The end is exclusive, so an event ending
// exactly at midnight (all-day or timed) doesn't spill onto the next day.
// All-day events and, without `tz`, timed ones use UTC days — the horizontal
// timeline's grid, which places events by raw instant. With `tz` (1W, which lays
// timed events out by that zone's days) a timed event keys on its zoned days.
export function eventDayKeys(ev: DisplayEvent, tz?: Timezone): string[] {
  const keys: string[] = [];
  const startMs = ev.start.getTime();
  const lastMs = Math.max(startMs, ev.end.getTime() - 1);
  const dayOf = (ms: number): number => {
    if (tz && !ev.allDay) {
      const p = zonedParts(new Date(ms), tz);
      return Date.UTC(p.y, p.m - 1, p.d);
    }
    const d = new Date(ms);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  };
  const last = dayOf(lastMs);
  for (let cursor = dayOf(startMs); cursor <= last; cursor += MS_PER_DAY) {
    keys.push(dayKeyOf(new Date(cursor)));
  }
  return keys;
}

// One blocked day of one event: the scan both views build their hatch sets from.
export type BlockedDay = { feedId: string; dayKey: string; density: 'thick' | 'thin'; global: boolean };

// Walk every day an event blocks across the visible feeds, with its density and
// scope. The timeline (header band + per-row hatch) and 1W (column + all-day
// hatch) each fold this into their own sets, so the rules for what blocks —
// hidden feeds, unblocked events, struck/hidden styles — live in one place.
export function forEachBlockedDay(
  feeds: readonly CalendarFeed[],
  eventsFor: (feedId: string) => readonly DisplayEvent[],
  visit: (day: BlockedDay) => void,
  tz?: Timezone,
): void {
  for (const feed of feeds) {
    if (feed.hidden) continue;
    for (const ev of eventsFor(feed.id)) {
      const block = effectiveBlock(ev, feed);
      if (block === 'none') continue;
      const density = hatchDensity(ev, feed);
      if (density === 'none') continue;
      const global = block === 'global';
      for (const dayKey of eventDayKeys(ev, tz)) visit({ feedId: feed.id, dayKey, density, global });
    }
  }
}

// The horizontal timeline's hatch sets. Two axes combine: the event's Block
// (effectiveBlock) decides the scope, its effective style the density (see
// hatchDensity). Global-block days span the whole timeline as a band — heavy
// (`band`) for thick events, discreet (`thinBand`) for thin ones, e.g. a muted
// rule with Global block — and tint the time header. Local-block days stay in
// their own row (`thickByFeed` / `thinByFeed`) and never touch the header.
export type TimelineHatch = {
  thickHeader: Set<string>;
  thinHeader: Set<string>;
  band: Set<string>;
  thinBand: Set<string>;
  thickByFeed: Record<string, Set<string>>;
  thinByFeed: Record<string, Set<string>>;
};

export function timelineHatch(
  feeds: readonly CalendarFeed[],
  eventsFor: (feedId: string) => readonly DisplayEvent[],
): TimelineHatch {
  const h: TimelineHatch = {
    thickHeader: new Set(),
    thinHeader: new Set(),
    band: new Set(),
    thinBand: new Set(),
    thickByFeed: {},
    thinByFeed: {},
  };
  forEachBlockedDay(feeds, eventsFor, ({ feedId, dayKey, density, global }) => {
    if (global) {
      (density === 'thick' ? h.thickHeader : h.thinHeader).add(dayKey);
      (density === 'thick' ? h.band : h.thinBand).add(dayKey);
    } else {
      ((density === 'thick' ? h.thickByFeed : h.thinByFeed)[feedId] ??= new Set()).add(dayKey);
    }
  });
  // A day already under the heavy band needs no discreet one on top.
  for (const k of h.band) h.thinBand.delete(k);
  return h;
}
